import React, { useCallback } from 'react';
import clsx from 'clsx';
import { Dropdown } from '@/components';
import { STUDENT_AVATAR_TONE_COUNT } from '../../boutique-classroom.constants';
import { PLANNED_LESSON_MAX_STUDENTS, PLANNED_LESSON_TEXT } from '../../components/planned-lesson-modal';
import { BoutiqueStudent, FilterOption, SortDirection, StudentSortKeyValue } from '../boutique-students.types';
import { GROUP_MENU_UPWARD_MIN_ROWS, GROUP_MENU_UPWARD_ROW_COUNT, GROUP_STUDENTS_LABEL, StudentSortKey, UNASSIGNED_GROUP_ID, UNASSIGNED_GROUP_LABEL } from '../boutique-students.constants';

interface StudentsTableProps {
	students: BoutiqueStudent[];
	groupOptions: FilterOption<number>[];
	selectedIds: number[];
	sortKey: StudentSortKeyValue | null;
	sortDirection: SortDirection;
	onToggleSelect: (studentId: number) => void;
	onToggleSelectAll: () => void;
	onSort: (key: StudentSortKeyValue) => void;
	onGroupChange: (studentId: number, groupId: number | null) => void;
	onGroupSelected: () => void;
	onPlanLesson: () => void;
}

const UNASSIGNED_OPTION: FilterOption<number> = { id: UNASSIGNED_GROUP_ID, name: UNASSIGNED_GROUP_LABEL };

function shouldOpenGroupMenuUpward(rowIndex: number, rowCount: number): boolean {
	return rowCount > GROUP_MENU_UPWARD_MIN_ROWS && rowIndex >= rowCount - GROUP_MENU_UPWARD_ROW_COUNT;
}

function getInitials(fullName: string): string {
	return fullName
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part.charAt(0).toLocaleUpperCase('tr-TR'))
		.join('');
}

function formatTarget(student: BoutiqueStudent): string {
	return `${student.targetUniversity} / ${student.targetDepartment}`;
}

export const StudentsTable: React.FC<StudentsTableProps> = ({
	students,
	groupOptions,
	selectedIds,
	sortKey,
	sortDirection,
	onToggleSelect,
	onToggleSelectAll,
	onSort,
	onGroupChange,
	onGroupSelected,
	onPlanLesson
}) => {
	const rowGroupOptions = [UNASSIGNED_OPTION, ...groupOptions];
	const selectedCount = selectedIds.length;
	const allVisibleSelected = students.length > 0 && students.every(student => selectedIds.includes(student.id));

	const renderSortIcon = useCallback(
		(key: StudentSortKeyValue) => (
			<i
				className={clsx('students-table__sort-icon', sortKey === key && sortDirection === 'desc' ? 'icons-alt-arrow-up' : 'icons-alt-arrow-down', {
					'students-table__sort-icon--active': sortKey === key
				})}
			/>
		),
		[sortDirection, sortKey]
	);

	return (
		<section className="students-table">
			<div className="students-table__toolbar">
				<label className="students-table__check">
					<input
						type="checkbox"
						checked={allVisibleSelected}
						onChange={onToggleSelectAll}
						aria-label="Tüm öğrencileri seç"
					/>
					<span
						className="students-table__check-mark"
						aria-hidden="true"
					/>
				</label>
				<span className={clsx('students-table__selection', { 'students-table__selection--active': selectedCount > 0 })}>
					{selectedCount > 0 ? `${selectedCount} öğrenci seçildi` : 'Öğrenci seçin'}
				</span>
				<button
					type="button"
					className="students-table__group-button"
					onClick={onGroupSelected}
					disabled={selectedCount === 0}>
					<i className="icons-users-group-rounded" />
					{GROUP_STUDENTS_LABEL}
				</button>
				<button
					type="button"
					className="students-table__group-button students-table__plan-button"
					onClick={onPlanLesson}
					disabled={selectedCount === 0 || selectedCount > PLANNED_LESSON_MAX_STUDENTS}
					title={selectedCount > PLANNED_LESSON_MAX_STUDENTS ? PLANNED_LESSON_TEXT.MAX_STUDENTS_HINT : undefined}>
					<i className="icons-plus" />
					{PLANNED_LESSON_TEXT.OPEN_BUTTON}
				</button>
			</div>

			<div className="students-table__head">
				<span className="students-table__cell students-table__cell--check" />
				<button
					type="button"
					className="students-table__cell students-table__cell--sortable"
					onClick={() => onSort(StudentSortKey.NAME)}>
					Öğrenci {renderSortIcon(StudentSortKey.NAME)}
				</button>
				<span className="students-table__cell">Sınıf</span>
				<span className="students-table__cell">Mevcut Grup</span>
				<span className="students-table__cell">Okul</span>
				<span className="students-table__cell">Hedef Üniversite / Bölüm</span>
			</div>

			{students.length === 0 ? (
				<div className="students-table__empty">Filtrelere uygun öğrenci bulunamadı.</div>
			) : (
				<ul className="students-table__body">
					{students.map((student, index) => {
						const isSelected = selectedIds.includes(student.id);
						const currentGroup = rowGroupOptions.find(option => option.id === (student.groupId ?? UNASSIGNED_GROUP_ID)) ?? UNASSIGNED_OPTION;
						return (
							<li
								key={student.id}
								className={clsx('students-table__row', { 'students-table__row--selected': isSelected })}>
								<label className="students-table__cell students-table__cell--check students-table__check">
									<input
										type="checkbox"
										checked={isSelected}
										onChange={() => onToggleSelect(student.id)}
										aria-label={`${student.fullName} seç`}
									/>
									<span
										className="students-table__check-mark"
										aria-hidden="true"
									/>
								</label>
								<span className="students-table__cell students-table__cell--student">
									<span className={clsx('students-table__avatar', `students-table__avatar--tone-${student.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(student.fullName)}</span>
									<span className="students-table__name">{student.fullName}</span>
								</span>
								<span
									className="students-table__cell"
									data-label="Sınıf">
									{student.gradeLabel}
								</span>
								<span
									className="students-table__cell students-table__cell--group"
									data-label="Mevcut Grup">
									<Dropdown<FilterOption<number>>
										className="students-table__group-select"
										options={rowGroupOptions}
										selectedOption={currentGroup}
										onOptionSelect={option => onGroupChange(student.id, option.id === UNASSIGNED_GROUP_ID ? null : option.id)}
										size="small"
										menuPosition={shouldOpenGroupMenuUpward(index, students.length) ? 'top' : 'bottom'}
									/>
								</span>
								<span
									className="students-table__cell"
									data-label="Okul">
									{student.schoolName}
								</span>
								<span
									className="students-table__cell"
									data-label="Hedef Üniversite / Bölüm">
									{formatTarget(student)}
								</span>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
};

export default StudentsTable;
